import { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../stores/auth.store';
import { IUser } from '../@shared/interfaces';

// Helper function to preload images
const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

interface NotificationSettings {
  email: boolean;
  push: boolean;
  marketing: boolean;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  bio: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

export const useSettings = () => {
  const profile = useAuthStore((state) => state.profile);
  const isLoading = useAuthStore((state) => state.isLoadingProfile);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const fetchProfile = useAuthStore((state) => state.fetchProfile);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    bio: '',
  });

  const [notifications, setNotifications] = useState<NotificationSettings>({
    email: true,
    push: false,
    marketing: false,
  });

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Update form data when profile is loaded
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        bio: profile.bio || '',
      });

      // Update notification settings from profile if available
      if (profile.settings?.preferences?.notifications) {
        setNotifications({
          email:
            profile.settings.preferences.notifications.channels?.email ?? true,
          push:
            profile.settings.preferences.notifications.channels?.push ?? false,
          marketing:
            profile.settings.preferences.notifications.subscriptions
              ?.promotions ?? false,
        });
      }

      // Preload profile image if available
      if (profile.photoUrl) {
        preloadImage(profile.photoUrl).catch((error) => {
          console.warn('Failed to preload profile image:', error);
        });
      }
    }
  }, [profile]);

  const handleInputChange = useCallback((event: any) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleNotificationChange = useCallback((event: any) => {
    const { name, checked } = event.target;
    setNotifications((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const showSnackbar = useCallback(
    (message: string, severity: SnackbarState['severity'] = 'success') => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const saveProfile = useCallback(
    async (fieldsToSave?: Partial<FormData>) => {
      const dataToSave = fieldsToSave || formData;

      // Validate required fields if saving name
      if (
        dataToSave.firstName !== undefined ||
        dataToSave.lastName !== undefined
      ) {
        if (!dataToSave.firstName?.trim() || !dataToSave.lastName?.trim()) {
          showSnackbar('First name and last name are required', 'error');
          return false;
        }
      }

      setIsUpdatingProfile(true);
      try {
        // Only send fields that have changed or are explicitly provided
        const updateData: Partial<IUser> = {};

        if (
          dataToSave.firstName !== undefined &&
          dataToSave.firstName !== profile?.firstName
        ) {
          updateData.firstName = dataToSave.firstName.trim();
        }
        if (
          dataToSave.lastName !== undefined &&
          dataToSave.lastName !== profile?.lastName
        ) {
          updateData.lastName = dataToSave.lastName.trim();
        }
        if (dataToSave.bio !== undefined && dataToSave.bio !== profile?.bio) {
          updateData.bio = dataToSave.bio.trim();
        }

        // If no changes, don't make API call
        if (Object.keys(updateData).length === 0) {
          showSnackbar('No changes to save', 'info');
          return true;
        }

        const result = await updateProfile(updateData);

        if (result) {
          showSnackbar('Profile updated successfully');
          return true;
        } else {
          showSnackbar('Failed to update profile', 'error');
          return false;
        }
      } catch (_error) {
        showSnackbar('Failed to update profile', 'error');
        return false;
      } finally {
        setIsUpdatingProfile(false);
      }
    },
    [formData, profile, updateProfile, showSnackbar]
  );

  // Convenience method for saving just name
  const saveName = useCallback(async () => {
    return saveProfile({
      firstName: formData.firstName,
      lastName: formData.lastName,
    });
  }, [saveProfile, formData.firstName, formData.lastName]);

  const refreshProfile = useCallback(async () => {
    try {
      await fetchProfile();
    } catch (_error) {
      showSnackbar('Failed to refresh profile', 'error');
    }
  }, [showSnackbar]); // Removed fetchProfile to prevent infinite loop

  const resetForm = useCallback(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        bio: profile.bio || '',
      });
    }
  }, [profile]);

  return {
    // State
    profile,
    isLoading,
    formData,
    notifications,
    snackbar,
    isUpdatingProfile,

    // Handlers
    handleInputChange,
    handleNotificationChange,
    showSnackbar,
    hideSnackbar,

    // Actions
    saveName,
    saveProfile,
    refreshProfile,
    resetForm,

    // Computed values
    hasUnsavedChanges: profile
      ? formData.firstName !== (profile.firstName || '') ||
        formData.lastName !== (profile.lastName || '') ||
        formData.bio !== (profile.bio || '')
      : false,

    isPhoneVerified:
      profile?.settings?.verifyMobileData?.isMobileVerified ?? false,
    // Show mobile or unverifiedMobile (whichever exists)
    phoneNumber:
      profile?.mobile || profile?.settings?.verifyMobileData?.unverifiedMobile,
    referralCode: profile?.referralCode,
  };
};
